import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { ErrorMessage, useFormikContext } from 'formik';
import React, { useMemo, useEffect } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { RootState } from '../../store/rootReducer';
import { AppDispatch } from '../../store/store';
import { CreateApplicationFormValues } from '../../views/ApplicationCreate';
import { ApplicationSchool, ApplicationIndustry } from '../../api/openapi';
import { fetchSchools } from '../../store/createApplicationSlice';

const ApplicationSignUpCreate = (): React.ReactElement => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation(['createApplication', 'common']);
  const { industries, industriesLoading, industriesError } = useSelector((state: RootState) => state.createApplication);
  const { schools, schoolsLoading, schoolsError } = useSelector((state: RootState) => state.createApplication);
  const { values, errors, touched, getFieldProps, setFieldValue } = useFormikContext<CreateApplicationFormValues>();

  const selectedSchool = useMemo<ApplicationSchool | undefined>(() => {
    if (schools != null && values.school != null) {
      return schools.find((s) => `${s.id}` === values.school);
    }

    return undefined;
  }, [schools, values.school]);

  useEffect(() => {
    if (values.industry) {
      dispatch(fetchSchools(values.industry.id));
    }
  }, [dispatch, values.industry]);

  useEffect(() => {
    setFieldValue('acceptedTerms', false);
  }, [values.industry, values.school, setFieldValue]);

  return (
    <Card className="mb-2">
      <Card.Body>
        <Row>
          <Col>
            <h4>{t('registration')}</h4>
          </Col>
        </Row>
        <Row>
          <Col md="4">
            <p className="small text-muted">{t('registrationHelp')}</p>
          </Col>
          <Col md="8">
            <Form.Group controlId="industry">
              <Form.Label>{t('industry')}*:</Form.Label>
              <Select<ApplicationIndustry>
                styles={{
                  // Fixes the overlapping problem of the component
                  menu: (provided) => ({ ...provided, zIndex: 2 }),
                }}
                options={industries || []}
                getOptionLabel={(i) => i.name}
                getOptionValue={(i) => `${i.id}`}
                value={values.industry}
                onChange={(v) => setFieldValue('industry', v)}
                placeholder={t('chooseIndustry')}
              />
              {/* <Form.Control
                as="select"
                className={classNames({
                  'is-invalid': touched.industry && errors.industry,
                })}
                {...getFieldProps('industry')}
              >
                <option value="">{t('chooseIndustry')}</option>
                {industries &&
                  industries.map((industry) => (
                    <option key={industry.id} value={industry.id}>
                      {industry.name}
                    </option>
                  ))}
              </Form.Control> */}
              {industriesError && <div className="invalid-feedback d-block text-left">{t('common:kerfisvilla')}</div>}
              {industriesLoading === 'idle' && industries?.length === 0 && <div className="invalid-feedback d-block text-left">{t('noIndustries')}</div>}
              <Form.Text className="text-muted">{industriesError === 'pending' && <FontAwesomeIcon spin icon={faSpinner} />}</Form.Text>
              <ErrorMessage name="industry" component="div" className="invalid-feedback d-block text-left" />
            </Form.Group>

            {values.industry && (
              <Form.Group controlId="school">
                <Form.Label>{t('school')}*:</Form.Label>
                <Form.Control
                  as="select"
                  className={classNames({
                    'is-invalid': touched.school && errors.school,
                  })}
                  {...getFieldProps('school')}
                >
                  <option value="">{t('chooseSchool')}</option>
                  {schools &&
                    schools.map((school) => (
                      <option key={school.id} value={school.id}>
                        {school.name}
                      </option>
                    ))}
                </Form.Control>
                {schoolsError && <div className="invalid-feedback d-block text-left">{t('common:kerfisvilla')}</div>}
                {schoolsLoading === 'idle' && schools?.length === 0 && <div className="invalid-feedback d-block text-left">{t('noSchools')}</div>}
                <Form.Text className="text-muted">{schoolsError === 'pending' && <FontAwesomeIcon spin icon={faSpinner} />}</Form.Text>
                <ErrorMessage name="school" component="div" className="invalid-feedback d-block text-left" />
                <br />
                {selectedSchool && selectedSchool.link && (
                  <>
                    <Form.Label>
                      {t('clickOnTheLinkToViewInfo')}:{' '}
                      <a href={selectedSchool.link} target="_blank" rel="noreferrer">
                        {t('infoAboutCost')}
                      </a>
                    </Form.Label>
                    <Form.Group controlId="formBasicCheckbox">
                      <Form.Check custom type="checkbox" label={t('iHaveReadTheInformation')} {...getFieldProps('acceptedTerms')} />
                    </Form.Group>
                  </>
                )}
              </Form.Group>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};
export default ApplicationSignUpCreate;
